import { memo, useRef, useEffect, useState } from "react";
import { Document, Thumbnail, pdfjs } from "react-pdf";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { cn } from "@core/utils/cn";

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// Each item: 31px top padding + 264px canvas + 31px bottom padding = 326px
const ITEM_HEIGHT = 326;
const THUMB_WIDTH = 187;
const OVERSCAN = 2;

type PdfThumbnailSidebarProps = {
  pdfFile: string | Blob;
  totalPages: number;
  currentPage: number;
  onPageClick: (page: number) => void;
};

export const PdfThumbnailSidebar = memo(function PdfThumbnailSidebar({
  pdfFile,
  totalPages,
  currentPage,
  onPageClick,
}: PdfThumbnailSidebarProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [windowStart, setWindowStart] = useState(0);
  const [windowEnd, setWindowEnd] = useState(OVERSCAN * 2 + 1);
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || totalPages === 0) return;

    const handleScroll = () => {
      if (rafIdRef.current !== null) return;

      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;

        const scrollTop = container.scrollTop;
        const height = container.clientHeight;

        const first = Math.max(
          0,
          Math.floor(scrollTop / ITEM_HEIGHT) - OVERSCAN,
        );
        const last = Math.min(
          totalPages - 1,
          Math.ceil((scrollTop + height) / ITEM_HEIGHT) + OVERSCAN,
        );

        setWindowStart(first);
        setWindowEnd(last);
      });
    };

    handleScroll();
    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [totalPages]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || totalPages === 0) return;

    const targetTop = (currentPage - 1) * ITEM_HEIGHT;
    const visibleTop = container.scrollTop;
    const visibleBottom = visibleTop + container.clientHeight;

    if (targetTop < visibleTop || targetTop + ITEM_HEIGHT > visibleBottom) {
      container.scrollTo({
        top: targetTop - container.clientHeight / 2 + ITEM_HEIGHT / 2,
        behavior: "smooth",
      });
    }
  }, [currentPage, totalPages]);

  if (totalPages === 0) return null;

  const windowPages = Array.from(
    { length: windowEnd - windowStart + 1 },
    (_, i) => windowStart + i + 1,
  );

  return (
    <aside
      ref={containerRef}
      className="relative w-[251px] flex-shrink-0 bg-white border border-neutral-200 rounded-xl overflow-y-auto"
    >
      <div style={{ height: totalPages * ITEM_HEIGHT }} />

      <div
        style={{
          position: "absolute",
          top: windowStart * ITEM_HEIGHT,
          left: 0,
          right: 0,
        }}
      >
        <Document file={pdfFile} loading={null} error={null}>
          {windowPages.map((pageNum) => (
            <div
              key={pageNum}
              className={cn(
                "px-[31px] pt-[31px] pb-[31px] relative cursor-pointer transition-colors",
                pageNum === currentPage
                  ? "bg-neutral-50"
                  : "hover:bg-neutral-50",
              )}
              style={{ height: ITEM_HEIGHT }}
              onClick={() => onPageClick(pageNum)}
            >
              <div
                className={cn(
                  "w-[187px] h-[264px] rounded-md border overflow-hidden",
                  pageNum === currentPage
                    ? "border-neutral-700"
                    : "border-neutral-100",
                )}
              >
                <Thumbnail
                  pageNumber={pageNum}
                  width={THUMB_WIDTH}
                  onItemClick={() => onPageClick(pageNum)}
                />
              </div>

              <span className="absolute bottom-[31px] right-[31px] bg-neutral-200 text-neutral-500 rounded-lg text-xs px-2.5 py-1">
                {pageNum}
              </span>
            </div>
          ))}
        </Document>
      </div>
    </aside>
  );
});
